import DOMPurify from 'dompurify'
import router from '../router'
import getCustomDomain from '../helpers/getCustomDomain'

const PURIFY_CONFIG = {
    ADD_ATTR: ['target', 'rel'],
    FORBID_TAGS: ['style', 'form', 'input', 'iframe', 'object', 'embed', 'link', 'meta'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'srcdoc', 'formaction']
}

//paths that are served by firebase hosting itself (cloud functions, the admin build, the
//startpage) instead of this app — links to them must do a real page load, even though the
//router's catch-all '/:companyId?' route would happily "match" them
const NON_APP_PATHS = ['/api', '/admin', '/dashboard', '/login', '/registration', '/auth', '/prnt', '/heartbeat', '/de', '/en', '/es']

export function sanitizeHtml(value) {
    if (value === null || value === undefined) {
        return ''
    }
    return DOMPurify.sanitize('' + value, PURIFY_CONFIG)
}

function isNonAppPath(path) {
    for (let nonAppPath of NON_APP_PATHS) {
        if (path == nonAppPath || path.startsWith(nonAppPath + '/')) {
            return true
        }
    }
    return false
}

//true if the url points at a page this app renders itself, so it can be opened with the
//router instead of reloading the whole page
function isInAppUrl(url) {
    if (url.origin != window.location.origin) {
        return false
    }
    if (url.protocol != 'http:' && url.protocol != 'https:') {
        //mailto:, tel:, … are not ours to handle
        return false
    }
    let path = url.pathname.replace(/\/+$/, '')
    if (path == '') {
        //'/' is the marketing startpage on the system domains, the shop itself on custom domains
        return Boolean(getCustomDomain())
    }
    if (isNonAppPath(path)) {
        return false
    }
    let resolved = router.resolve(url.pathname + url.search + url.hash)
    return Boolean(resolved && resolved.matched && resolved.matched.length > 0)
}

//the rtf editor puts target="_blank" on every link. That is right for links leaving the
//shop, but wrong for links back into it: those should stay in the current window and be
//handled by the router, so their target is dropped again here.
function normalizeLinkTargets(el) {
    for (let anchor of el.querySelectorAll('a[href]')) {
        let url = null
        try {
            url = new URL(anchor.href, window.location.href)
        } catch (error) {
            continue
        }
        if (isInAppUrl(url)) {
            anchor.removeAttribute('target')
            continue
        }
        if (url.protocol != 'http:' && url.protocol != 'https:') {
            //mailto:, tel:, … are handed to the os, a new tab would just stay blank
            continue
        }
        anchor.setAttribute('target', '_blank')
        anchor.setAttribute('rel', 'noopener noreferrer')
    }
}

//links inside sanitized html (product descriptions, notices, content pages) are plain
//anchors — without this every internal link would tear down the app and reload the page
function onSafeHtmlClick(event) {
    let anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null
    if (!anchor || !event.currentTarget.contains(anchor)) {
        return
    }

    //the click belongs to the link alone: whatever the text sits in must not act on it as
    //well (a product row opening its details modal, a card navigating somewhere, …).
    //stopped for every kind of link click — following it in a new tab or downloading it is
    //just as much "not a click on the surrounding element" as opening it right here.
    event.stopPropagation()

    if (event.defaultPrevented || event.button !== 0) {
        return
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        //open in a new tab/window/download: leave it to the browser
        return
    }
    if (anchor.hasAttribute('download')) {
        return
    }
    //a target (usually _blank, the editor sets it on every link) is deliberately ignored here:
    //internal links stay in this window, external ones keep their target and open in a new tab

    let url = null
    try {
        url = new URL(anchor.href, window.location.href)
    } catch (error) {
        return
    }

    if (url.hash && url.pathname == window.location.pathname && url.search == window.location.search) {
        //an anchor within the current page: the browser scrolls to it on its own
        return
    }
    if (!isInAppUrl(url)) {
        return
    }

    event.preventDefault()
    //a duplicated navigation resolves with a navigation failure, it doesn't throw
    router.push(url.pathname + url.search + url.hash).catch(() => { })
}

export const safeHtmlDirective = {
    beforeMount(el, binding) {
        el.innerHTML = sanitizeHtml(binding.value)
        normalizeLinkTargets(el)
        el.addEventListener('click', onSafeHtmlClick)
    },
    updated(el, binding) {
        if (binding.value === binding.oldValue) {
            return
        }
        el.innerHTML = sanitizeHtml(binding.value)
        normalizeLinkTargets(el)
    },
    unmounted(el) {
        el.removeEventListener('click', onSafeHtmlClick)
    }
}
